import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  updateTask,
  type Task,
  type TaskListResult,
  type UpdateTaskInput,
} from '../../../lib/tasksApi';

/**
 * CLE-174 — optimistic task PATCH for the inline editors.
 *
 * Linear applies field edits instantly: clicking a priority/status/assignee/due
 * option updates the row before the network round-trip. This mutation patches
 * every cached `['tasks', …]` list query in place, then reconciles with the
 * server response (or rolls back on error). The caller also passes an optional
 * `patch` describing how to mutate the local Task — needed for fields like
 * assignee/labels where the input id differs from the denormalised display
 * object stored on the Task.
 */

export interface OptimisticUpdateVars {
  id: string;
  input: UpdateTaskInput;
  /** Local mutation applied to the cached Task for instant UI feedback. */
  patch: (task: Task) => Task;
}

type ListSnapshot = Array<[readonly unknown[], TaskListResult | undefined]>;

export function useOptimisticTaskUpdate() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: OptimisticUpdateVars) => updateTask(id, input),

    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const previous = qc.getQueriesData<TaskListResult>({ queryKey: ['tasks'] }) as ListSnapshot;

      for (const [key, data] of previous) {
        if (!data) continue;
        qc.setQueryData<TaskListResult>(key, {
          ...data,
          tasks: data.tasks.map((t) => (t.id === id ? patch(t) : t)),
        });
      }

      // Patch the single-task cache too so an open detail view stays in sync.
      const prevTask = qc.getQueryData<Task>(['task', id]);
      if (prevTask) qc.setQueryData<Task>(['task', id], patch(prevTask));

      return { previous, prevTask };
    },

    onError: (_err, vars, ctx) => {
      // Roll back every list we touched.
      ctx?.previous?.forEach(([key, data]) => qc.setQueryData(key, data));
      if (ctx?.prevTask) qc.setQueryData(['task', vars.id], ctx.prevTask);
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task-estimate-totals'] });
    },
  });
}
